import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Form,
  FormFeedback,
  Alert,
  Spinner,
  InputGroup,
  InputGroupText,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import ParticlesAuth from "../../Components/Common/ParticlesAuth";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import classnames from "classnames";
import logoLight from "../../assets/images/full_logo.png";
import tenantService from '../../services/tenant.service';
import { handleApiResponse, handleApiError, ApiResponse } from '../../utils/apiResponseHandler';
import { redirectToTenantSubdomain } from '../../utils/tenantContext';
import PricingCalculator from '../../Components/Common/PricingCalculator';
import { MultiRegimePricingBreakdown, RegimeTributario, formatCurrency, REGIME_BASE_PRICES } from '../../models/PricingModel';
import TenantProvisioningProgress from '../../Components/Common/TenantProvisioningProgress';

const TenantSignup = () => {
  const [loader, setLoader] = useState<boolean>(false);
  const [passwordShow, setPasswordShow] = useState<boolean>(false);
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [subdomainAvailable, setSubdomainAvailable] = useState<boolean | null>(null);
  const [checkingSubdomain, setCheckingSubdomain] = useState<boolean>(false);
  const [pricingBreakdown, setPricingBreakdown] = useState<MultiRegimePricingBreakdown | null>(null);
  const [lookingUpCnpj, setLookingUpCnpj] = useState<boolean>(false);

  // Provisioning state
  const [isProvisioning, setIsProvisioning] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState<string>('');

  // Wizard state
  const [activeTab, setActiveTab] = useState<number>(1);
  const [passedSteps, setPassedSteps] = useState<number[]>([1]);

  // Validation schema
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      cnpj: '',
      name: '',
      subdomain: '',
      contactName: '',
      contactEmail: '',
      contactPassword: '',
      confirmPassword: '',
      plan: 'SimplesNacional' as RegimeTributario,
      agreeTerms: false,
    },
    validationSchema: Yup.object({
      cnpj: Yup.string()
        .required('CNPJ é obrigatório')
        .matches(/^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/, 'CNPJ inválido. Use o formato: 00.000.000/0000-00'),
      name: Yup.string()
        .required('Nome da empresa é obrigatório')
        .min(3, 'Nome deve ter pelo menos 3 caracteres')
        .max(100, 'Nome deve ter no máximo 100 caracteres'),
      subdomain: Yup.string()
        .required('Subdomínio é obrigatório')
        .matches(/^[a-z0-9-]+$/, 'Subdomínio deve conter apenas letras minúsculas, números e hífens')
        .min(3, 'Subdomínio deve ter pelo menos 3 caracteres')
        .max(50, 'Subdomínio deve ter no máximo 50 caracteres'),
      contactName: Yup.string()
        .required('Nome do responsável é obrigatório')
        .min(3, 'Nome deve ter pelo menos 3 caracteres'),
      contactEmail: Yup.string()
        .required('Email é obrigatório')
        .email('Email inválido'),
      contactPassword: Yup.string()
        .required('Senha é obrigatória')
        .min(8, 'Senha deve ter pelo menos 8 caracteres')
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
          'Senha deve conter letras maiúsculas, minúsculas, números e caracteres especiais'
        ),
      confirmPassword: Yup.string()
        .required('Confirmação de senha é obrigatória')
        .oneOf([Yup.ref('contactPassword')], 'As senhas não coincidem'),
      agreeTerms: Yup.boolean()
        .oneOf([true], 'Você deve aceitar os termos de uso'),
    }),
    onSubmit: async (values) => {
      setLoader(true);
      setErrorMsg('');
      setSuccessMsg('');

      try {
        // Ensure pricing is calculated
        if (!pricingBreakdown) {
          setErrorMsg('Por favor, configure o preço');
          setLoader(false);
          return;
        }

        // Ensure at least one company is selected
        if (pricingBreakdown.totalCompanies === 0) {
          setErrorMsg('Por favor, selecione pelo menos uma empresa');
          setLoader(false);
          return;
        }

        // Note: Trial validation (max 3 companies per regime) is done in nextStep() before reaching this tab

        // Generate sessionId for SignalR progress tracking
        const provisioningSessionId = `tenant-signup-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        setSessionId(provisioningSessionId);
        setIsProvisioning(true);

        const result = await tenantService.signupTenant({
          cnpj: values.cnpj,
          name: values.name,
          contactName: values.contactName,
          contactEmail: values.contactEmail,
          contactPassword: values.contactPassword,
          plan: 0, // Starter plan (0 = TenantPlan.Starter enum value)
          subdomain: values.subdomain,
          sessionId: provisioningSessionId,
        });

        if (result && typeof result === 'object' && 'severity' in result && 'success' in result) {
          const apiResponse = result as ApiResponse;
          handleApiResponse(apiResponse);

          if (apiResponse.severity === 'success' && apiResponse.data) {
            const { subdomain, accessToken } = apiResponse.data;

            // Store access token
            sessionStorage.setItem('authUser', JSON.stringify({
              token: accessToken,
              email: values.contactEmail,
            }));

            setSuccessMsg(`Conta criada com sucesso! Redirecionando para ${subdomain}.atoscloud.app...`);

            // Redirect to tenant subdomain after 2 seconds
            setTimeout(() => {
              redirectToTenantSubdomain(subdomain, '/dashboard');
            }, 2000);
          }
        }
      } catch (error: any) {
        handleApiError(error, 'Erro ao criar conta');
        setErrorMsg(error.message || 'Erro ao criar conta. Tente novamente.');
        setIsProvisioning(false);
      } finally {
        setLoader(false);
      }
    },
  });

  // Format CNPJ as user types
  const formatCnpj = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 14) {
      return digits
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    return value;
  };

  // Lookup CNPJ from Receita Federal
  useEffect(() => {
    const cnpj = validation.values.cnpj;
    const cnpjDigits = cnpj.replace(/\D/g, '');

    if (cnpjDigits.length !== 14) {
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLookingUpCnpj(true);
      try {
        const result = await tenantService.lookupCnpj(cnpjDigits);

        if (result && typeof result === 'object' && 'severity' in result) {
          const apiResponse = result as ApiResponse;
          if (apiResponse.severity === 'success' && apiResponse.data) {
            // Fill company name from CNPJ lookup
            const companyData = apiResponse.data;
            validation.setFieldValue('name', companyData.razaoSocial || companyData.nomeFantasia || '');
          }
        }
      } catch (error) {
        console.error('Error looking up CNPJ:', error);
      } finally {
        setLookingUpCnpj(false);
      }
    }, 800);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validation.values.cnpj]);

  // Auto-generate subdomain from name
  useEffect(() => {
    if (validation.values.name && !validation.touched.subdomain) {
      const generatedSubdomain = validation.values.name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove accents
        .replace(/[^a-z0-9\\s-]/g, '') // Remove special characters
        .replace(/\\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Remove consecutive hyphens
        .substring(0, 50);

      if (generatedSubdomain !== validation.values.subdomain) {
        validation.setFieldValue('subdomain', generatedSubdomain);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validation.values.name]);

  // Check subdomain availability with debounce
  useEffect(() => {
    const subdomain = validation.values.subdomain;

    if (!subdomain || subdomain.length < 3) {
      setSubdomainAvailable(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setCheckingSubdomain(true);
      try {
        const result = await tenantService.checkSubdomainAvailability(subdomain);

        if (result && typeof result === 'object' && 'severity' in result) {
          const apiResponse = result as ApiResponse;
          if (apiResponse.severity === 'success' && apiResponse.data) {
            setSubdomainAvailable(apiResponse.data.isAvailable);
          }
        }
      } catch (error) {
        console.error('Error checking subdomain:', error);
      } finally {
        setCheckingSubdomain(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [validation.values.subdomain]);

  const handlePricingChange = useCallback((breakdown: MultiRegimePricingBreakdown) => {
    setPricingBreakdown(breakdown);
  }, []);

  // Wizard tab toggle
  function toggleTab(tab: number) {
    if (activeTab !== tab) {
      if (tab >= 1 && tab <= 3) {
        const modifiedSteps = [...passedSteps];
        if (!modifiedSteps.includes(tab)) {
          modifiedSteps.push(tab);
        }
        setPassedSteps(modifiedSteps);
        setActiveTab(tab);
      }
    }
  }

  // Validate step before proceeding
  const canProceedToStep = (step: number): boolean => {
    if (step === 2) {
      // Check if step 1 fields are valid
      return !!(
        validation.values.cnpj &&
        validation.values.name &&
        validation.values.subdomain &&
        validation.values.contactName &&
        validation.values.contactEmail &&
        validation.values.contactPassword &&
        validation.values.confirmPassword &&
        subdomainAvailable === true &&
        !validation.errors.cnpj &&
        !validation.errors.name &&
        !validation.errors.subdomain &&
        !validation.errors.contactName &&
        !validation.errors.contactEmail &&
        !validation.errors.contactPassword &&
        !validation.errors.confirmPassword
      );
    }
    if (step === 3) {
      // Check if pricing is configured
      return !!(pricingBreakdown && pricingBreakdown.totalCompanies > 0);
    }
    return true;
  };

  const nextStep = () => {
    // Clear previous errors
    setErrorMsg('');

    // Validate before proceeding
    if (activeTab < 3 && canProceedToStep(activeTab + 1)) {
      // Special validation when moving from Tab 2 (Plano) to Tab 3 (Finalizar)
      if (activeTab === 2) {
        // Trial limitation: Maximum 3 companies per regime
        const TRIAL_MAX_COMPANIES_PER_REGIME = 3;
        const regimesExceedingLimit: string[] = [];

        if (pricingBreakdown) {
          pricingBreakdown.regimeDetails.forEach(regime => {
            if (regime.companyCount > TRIAL_MAX_COMPANIES_PER_REGIME) {
              const regimeName = REGIME_BASE_PRICES.find(r => r.regime === regime.regime)?.displayName || regime.regime;
              regimesExceedingLimit.push(`${regimeName} (${regime.companyCount} empresas)`);
            }
          });
        }

        if (regimesExceedingLimit.length > 0) {
          const errorMessage =
            `Para o período de teste gratuito, você pode adicionar no máximo ${TRIAL_MAX_COMPANIES_PER_REGIME} empresas por regime tributário.\n\n` +
            `Regimes que excedem o limite:\n` +
            regimesExceedingLimit.map(r => `• ${r}`).join('\n') +
            `\n\nPor favor, ajuste a quantidade de empresas ou entre em contato para um plano customizado.`;

          setErrorMsg(errorMessage);

          // Scroll to top to show the error message
          window.scrollTo({ top: 0, behavior: 'smooth' });

          return; // Don't proceed to next tab
        }
      }

      toggleTab(activeTab + 1);
    } else if (activeTab < 3) {
      setErrorMsg('Por favor, preencha todos os campos obrigatórios antes de continuar');
    }
  };

  const prevStep = () => {
    setErrorMsg(''); // Clear errors when going back
    if (activeTab > 1) {
      toggleTab(activeTab - 1);
    }
  };

  document.title = "Criar Conta | Atos Sistema Contábil";

  return (
    <React.Fragment>
      <style>{`
        @media (max-width: 576px) {
          .auth-page-content-mobile {
            margin-top: -40px !important;
          }
          .auth-logo-mobile {
            margin-top: 30px !important;
            margin-bottom: 0 !important;
          }
          .auth-logo-mobile img {
            height: 120px !important;
            max-width: 90vw !important;
          }
          .auth-card-mobile {
            margin-top: 0.75rem !important;
          }
        }
        @media (min-width: 577px) {
          .auth-page-content-mobile {
            margin-top: -80px !important;
          }
          .auth-logo-mobile {
            margin-top: 40px !important;
          }
          .auth-logo-mobile img {
            height: 240px !important;
          }
          .auth-card-mobile {
            margin-top: 1rem !important;
          }
        }
      `}</style>
      <ParticlesAuth>
        <div className="auth-page-content auth-page-content-mobile">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-2 text-white-50 auth-logo-mobile">
                  <div>
                    <Link to="/" className="d-inline-block auth-logo" style={{ background: 'transparent' }}>
                      <img src={`${logoLight}?v=${Date.now()}`} alt="AtosCloud Logo" style={{ background: 'transparent', display: 'block' }} />
                    </Link>
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={10} lg={10} xl={9}>
                <Card className="mt-4 auth-card-mobile">
                  <CardBody className="p-4">
                    <div className="text-center mt-2">
                      <h5 className="text-primary">Crie sua Conta</h5>
                      <p className="text-muted">Comece seu teste gratuito de 14 dias</p>
                    </div>

                    {successMsg && (
                      <Alert color="success" className="mt-3">
                        {successMsg}
                      </Alert>
                    )}

                    {errorMsg && (
                      <Alert color="danger" className="mt-3" style={{ whiteSpace: 'pre-line' }}>
                        {errorMsg}
                      </Alert>
                    )}

                    <div className="p-2 mt-4">
                      <Form onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}>
                        {/* Step Arrow Nav */}
                        <div className="step-arrow-nav mb-4">
                          <Nav
                            className="nav-pills nav-justified custom-nav"
                            role="tablist"
                          >
                            <NavItem role="presentation">
                              <NavLink
                                href="#"
                                className={classnames(
                                  {
                                    active: activeTab === 1,
                                    done: activeTab <= 3 && activeTab >= 0,
                                  },
                                  "p-3"
                                )}
                                onClick={(e) => {
                                  e.preventDefault();
                                  toggleTab(1);
                                }}
                              >
                                <i className="ri-building-line fs-16 p-2 bg-primary-subtle text-primary rounded-circle align-middle me-2"></i>
                                <span className="d-none d-sm-inline">Empresa</span>
                              </NavLink>
                            </NavItem>
                            <NavItem role="presentation">
                              <NavLink
                                href="#"
                                className={classnames(
                                  {
                                    active: activeTab === 2,
                                    done: activeTab <= 3 && activeTab > 1,
                                  },
                                  "p-3"
                                )}
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (canProceedToStep(2)) {
                                    toggleTab(2);
                                  }
                                }}
                              >
                                <i className="ri-price-tag-3-line fs-16 p-2 bg-primary-subtle text-primary rounded-circle align-middle me-2"></i>
                                <span className="d-none d-sm-inline">Plano</span>
                              </NavLink>
                            </NavItem>
                            <NavItem role="presentation">
                              <NavLink
                                href="#"
                                className={classnames(
                                  {
                                    active: activeTab === 3,
                                    done: activeTab <= 3 && activeTab > 2,
                                  },
                                  "p-3"
                                )}
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (canProceedToStep(3)) {
                                    toggleTab(3);
                                  }
                                }}
                              >
                                <i className="ri-checkbox-circle-line fs-16 p-2 bg-primary-subtle text-primary rounded-circle align-middle me-2"></i>
                                <span className="d-none d-sm-inline">Finalizar</span>
                              </NavLink>
                            </NavItem>
                          </Nav>
                        </div>

                        <TabContent activeTab={activeTab}>
                          {/* Tab 1: Empresa */}
                          <TabPane tabId={1}>
                            <h6 className="mb-3 text-muted">Informações da Empresa</h6>

                            <div className="mb-3">
                              <Label htmlFor="cnpj" className="form-label">
                                CNPJ <span className="text-danger">*</span>
                              </Label>
                              <InputGroup>
                                <Input
                                  name="cnpj"
                                  type="text"
                                  placeholder="00.000.000/0000-00"
                                  onChange={(e) => {
                                    const formatted = formatCnpj(e.target.value);
                                    validation.setFieldValue('cnpj', formatted);
                                  }}
                                  onBlur={validation.handleBlur}
                                  value={validation.values.cnpj || ""}
                                  invalid={!!(validation.touched.cnpj && validation.errors.cnpj)}
                                  maxLength={18}
                                />
                                {lookingUpCnpj && (
                                  <InputGroupText>
                                    <Spinner size="sm" />
                                  </InputGroupText>
                                )}
                                {validation.touched.cnpj && validation.errors.cnpj && (
                                  <FormFeedback type="invalid">{validation.errors.cnpj}</FormFeedback>
                                )}
                              </InputGroup>
                              {lookingUpCnpj && (
                                <small className="text-muted">
                                  <i className="ri-search-line me-1"></i>Buscando dados na Receita Federal...
                                </small>
                              )}
                            </div>

                            <div className="mb-3">
                              <Label htmlFor="name" className="form-label">
                                Nome da Empresa <span className="text-danger">*</span>
                              </Label>
                              <Input
                                name="name"
                                type="text"
                                placeholder="Será preenchido automaticamente após busca do CNPJ"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.name || ""}
                                invalid={!!(validation.touched.name && validation.errors.name)}
                                disabled={true}
                              />
                              {validation.touched.name && validation.errors.name && (
                                <FormFeedback type="invalid">{validation.errors.name}</FormFeedback>
                              )}
                              {validation.values.name && (
                                <small className="text-success">
                                  <i className="ri-check-line me-1"></i>Nome preenchido pela Receita Federal
                                </small>
                              )}
                            </div>

                            <div className="mb-3">
                              <Label htmlFor="subdomain" className="form-label">
                                Subdomínio <span className="text-danger">*</span>
                              </Label>
                              <InputGroup>
                                <Input
                                  name="subdomain"
                                  type="text"
                                  placeholder="sua-empresa"
                                  onChange={validation.handleChange}
                                  onBlur={validation.handleBlur}
                                  value={validation.values.subdomain || ""}
                                  invalid={!!(validation.touched.subdomain && validation.errors.subdomain)}
                                />
                                <InputGroupText>.atoscloud.app</InputGroupText>
                                {validation.touched.subdomain && validation.errors.subdomain && (
                                  <FormFeedback type="invalid">{validation.errors.subdomain}</FormFeedback>
                                )}
                              </InputGroup>
                              {checkingSubdomain && (
                                <small className="text-muted">
                                  <Spinner size="sm" className="me-1" /> Verificando disponibilidade...
                                </small>
                              )}
                              {!checkingSubdomain && subdomainAvailable === true && (
                                <small className="text-success">✓ Subdomínio disponível!</small>
                              )}
                              {!checkingSubdomain && subdomainAvailable === false && (
                                <small className="text-danger">✗ Subdomínio já está em uso</small>
                              )}
                            </div>

                            <h6 className="mb-3 mt-4 text-muted">Dados do Responsável</h6>

                            <div className="mb-3">
                              <Label htmlFor="contactName" className="form-label">
                                Nome Completo <span className="text-danger">*</span>
                              </Label>
                              <Input
                                name="contactName"
                                type="text"
                                placeholder="Digite seu nome completo"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.contactName || ""}
                                invalid={!!(validation.touched.contactName && validation.errors.contactName)}
                              />
                              {validation.touched.contactName && validation.errors.contactName && (
                                <FormFeedback type="invalid">{validation.errors.contactName}</FormFeedback>
                              )}
                            </div>

                            <div className="mb-3">
                              <Label htmlFor="contactEmail" className="form-label">
                                Email <span className="text-danger">*</span>
                              </Label>
                              <Input
                                name="contactEmail"
                                type="email"
                                placeholder="Digite seu email"
                                autoComplete="off"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.contactEmail || ""}
                                invalid={!!(validation.touched.contactEmail && validation.errors.contactEmail)}
                              />
                              {validation.touched.contactEmail && validation.errors.contactEmail && (
                                <FormFeedback type="invalid">{validation.errors.contactEmail}</FormFeedback>
                              )}
                            </div>

                            <div className="mb-3">
                              <Label htmlFor="contactPassword" className="form-label">
                                Senha <span className="text-danger">*</span>
                              </Label>
                              <InputGroup>
                                <Input
                                  name="contactPassword"
                                  type={passwordShow ? "text" : "password"}
                                  placeholder="Digite sua senha"
                                  autoComplete="new-password"
                                  onChange={validation.handleChange}
                                  onBlur={validation.handleBlur}
                                  value={validation.values.contactPassword || ""}
                                  invalid={!!(validation.touched.contactPassword && validation.errors.contactPassword)}
                                />
                                <Button
                                  color="light"
                                  onClick={() => setPasswordShow(!passwordShow)}
                                  type="button"
                                >
                                  <i className={passwordShow ? "ri-eye-off-fill" : "ri-eye-fill"} />
                                </Button>
                                {validation.touched.contactPassword && validation.errors.contactPassword && (
                                  <FormFeedback type="invalid">{validation.errors.contactPassword}</FormFeedback>
                                )}
                              </InputGroup>
                              <small className="text-muted">
                                <i className="ri-information-line me-1"></i>
                                Senha deve conter: mínimo 8 caracteres, letras maiúsculas e minúsculas, números e caracteres especiais (@$!%*?&)
                              </small>
                            </div>

                            <div className="mb-3">
                              <Label htmlFor="confirmPassword" className="form-label">
                                Confirmar Senha <span className="text-danger">*</span>
                              </Label>
                              <Input
                                name="confirmPassword"
                                type="password"
                                placeholder="Confirme sua senha"
                                autoComplete="new-password"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.confirmPassword || ""}
                                invalid={!!(validation.touched.confirmPassword && validation.errors.confirmPassword)}
                              />
                              {validation.touched.confirmPassword && validation.errors.confirmPassword && (
                                <FormFeedback type="invalid">{validation.errors.confirmPassword}</FormFeedback>
                              )}
                            </div>

                            <div className="d-flex justify-content-end gap-2 mt-4">
                              <Button
                                color="primary"
                                onClick={nextStep}
                                disabled={!canProceedToStep(2)}
                              >
                                Próximo <i className="ri-arrow-right-line align-middle ms-1"></i>
                              </Button>
                            </div>
                          </TabPane>

                          {/* Tab 2: Plano */}
                          <TabPane tabId={2}>
                            <div className="mb-4">
                              <PricingCalculator
                                onPricingChange={handlePricingChange}
                                initialCompanyCounts={{
                                  MEI: 0,
                                  SimplesNacional: 0,
                                  LucroPresumido: 0,
                                  LucroReal: 0,
                                }}
                                initialVolume={{
                                  notasFiscaisMensal: 0,
                                  itensMediaPorNota: 0,
                                  funcionariosAtivos: 0,
                                }}
                              />
                            </div>

                            <div className="d-flex justify-content-between gap-2 mt-4">
                              <Button
                                color="light"
                                onClick={prevStep}
                              >
                                <i className="ri-arrow-left-line align-middle me-1"></i> Voltar
                              </Button>
                              <Button
                                color="primary"
                                onClick={nextStep}
                                disabled={!canProceedToStep(3)}
                              >
                                Próximo <i className="ri-arrow-right-line align-middle ms-1"></i>
                              </Button>
                            </div>
                          </TabPane>

                          {/* Tab 3: Finalizar */}
                          <TabPane tabId={3}>
                            <div className="mb-4">
                              <h6 className="mb-3 text-primary">Resumo do Cadastro</h6>

                              <div className="alert alert-info">
                                <h6 className="alert-heading">
                                  <i className="ri-gift-line me-2"></i>Teste Gratuito de 14 Dias
                                </h6>
                                <p className="mb-0">
                                  Você receberá <strong>14 dias de teste gratuito</strong> com acesso total à plataforma.
                                  Não é necessário cartão de crédito para começar!
                                </p>
                              </div>

                              {!isProvisioning && pricingBreakdown && (
                                <Card className="border">
                                  <CardBody>
                                    <h6 className="mb-3">Detalhes da Assinatura</h6>
                                    <div className="table-responsive">
                                      <table className="table table-sm mb-0">
                                        <tbody>
                                          <tr>
                                            <td>Empresa:</td>
                                            <td className="text-end fw-semibold">{validation.values.name}</td>
                                          </tr>
                                          <tr>
                                            <td>Subdomínio:</td>
                                            <td className="text-end fw-semibold">{validation.values.subdomain}.atoscloud.app</td>
                                          </tr>
                                          <tr>
                                            <td>Total de Empresas:</td>
                                            <td className="text-end fw-semibold">{pricingBreakdown.totalCompanies}</td>
                                          </tr>
                                          <tr>
                                            <td>Valor Mensal:</td>
                                            <td className="text-end fw-semibold text-success">
                                              {formatCurrency(pricingBreakdown.totalMonthly)}
                                            </td>
                                          </tr>
                                          <tr className="table-active">
                                            <td className="fw-bold">Período de Teste:</td>
                                            <td className="text-end fw-bold text-primary">GRÁTIS até {new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toLocaleDateString('pt-BR')}</td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>
                                  </CardBody>
                                </Card>
                              )}

                              {isProvisioning && sessionId && (
                                <Card className="border border-primary">
                                  <CardBody>
                                    <h6 className="mb-3">
                                      <i className="ri-settings-4-line me-2"></i>
                                      Criando sua Conta
                                    </h6>
                                    <TenantProvisioningProgress
                                      sessionId={sessionId}
                                      onComplete={() => {
                                        // Completion is handled in onSubmit callback
                                      }}
                                      onError={(error) => {
                                        setErrorMsg(error);
                                        setIsProvisioning(false);
                                      }}
                                    />
                                  </CardBody>
                                </Card>
                              )}

                              {!isProvisioning && <div className="form-check mb-3">
                                <Input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="agreeTerms"
                                  name="agreeTerms"
                                  onChange={validation.handleChange}
                                  onBlur={validation.handleBlur}
                                  checked={validation.values.agreeTerms}
                                  invalid={!!(validation.touched.agreeTerms && validation.errors.agreeTerms)}
                                />
                                <Label className="form-check-label" htmlFor="agreeTerms">
                                  Eu aceito os <Link to="/terms" className="text-decoration-underline">Termos de Uso</Link> e a{' '}
                                  <Link to="/privacy" className="text-decoration-underline">Política de Privacidade</Link>
                                </Label>
                                {validation.touched.agreeTerms && validation.errors.agreeTerms && (
                                  <FormFeedback type="invalid" className="d-block">
                                    {validation.errors.agreeTerms}
                                  </FormFeedback>
                                )}
                              </div>}
                            </div>

                            {!isProvisioning && <div className="d-flex justify-content-between gap-2 mt-4">
                              <Button
                                color="light"
                                onClick={prevStep}
                              >
                                <i className="ri-arrow-left-line align-middle me-1"></i> Voltar
                              </Button>
                              <Button
                                color="success"
                                type="submit"
                                disabled={loader || subdomainAvailable === false || !validation.values.agreeTerms}
                              >
                                {loader ? (
                                  <>
                                    <Spinner size="sm" className="me-2" />
                                    Criando conta...
                                  </>
                                ) : (
                                  <>
                                    <i className="ri-check-line me-1"></i>
                                    Criar Conta e Iniciar Teste Gratuito
                                  </>
                                )}
                              </Button>
                            </div>}
                          </TabPane>
                        </TabContent>
                      </Form>
                    </div>

                    <div className="mt-4 text-center">
                      <p className="mb-0">
                        Já tem uma conta?{' '}
                        <Link to="/login" className="fw-semibold text-primary text-decoration-underline">
                          Entrar
                        </Link>
                      </p>
                    </div>
                  </CardBody>
                </Card>

                <div className="mt-4 text-center">
                  <p className="mb-0 text-muted">
                    © {new Date().getFullYear()} Atos Sistema Contábil. Todos os direitos reservados.
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default TenantSignup;
